import { NgModule, APP_INITIALIZER } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { RouteService } from './base/services/routes/routes.service';
import { AppidService } from './base/services/appid/appid.service';

const GIGMONSTER_APP_ID = 434693;

// SuperstructureComponent and SuperstructureEasysuiteComponent are deliberately not imported at
// the top of this file. Doing so closes an import cycle -- app-routing -> superstructure ->
// SuperstructureBaseComponent -> AppComponent -> AppModule -> app-routing -- and whichever module
// the bundler evaluates first sees the others as undefined. They are imported dynamically inside
// initializeUnauthNavRoutes instead, which runs long after every module has finished evaluating.

/**
 * Rebuilds a router config from the copy NavService writes to localStorage on every nav update.
 *
 * The cache exists because the router config is held in memory only: a browser refresh would
 * otherwise drop every route the signed-in user's role earned them. Route components are class
 * references and do not survive JSON.stringify, so the cached entries come back without one and
 * it has to be reattached here. Redirect routes are skipped -- a route may carry a component or
 * a redirectTo, never both.
 *
 * @param component The superstructure component to render the cached routes with.
 * @returns The cached routes, or null when nothing usable is cached.
 */
function readCachedRoutes(component: any): Routes | null {
    try {
        const cached = JSON.parse(localStorage.getItem('routes'));
        if (!Array.isArray(cached) || cached.length === 0) {
            return null;
        }

        cached.forEach(route => {
            if (!route.redirectTo) {
                route.component = component;
            }
        });

        return cached;
    } catch (error) {
        console.warn('Could not restore cached routes:', error);
        return null;
    }
}

/**
 * Installs the router config, then performs the application's first navigation.
 *
 * The app has no compile-time routes. Which paths exist depends on the app the hostname resolves
 * to and on the signed-in user's role, so the config is assembled here at startup -- from the
 * localStorage cache when one is present, otherwise from the unauthenticated set the backend
 * returns from /auth/routes.
 *
 * Nothing can be rendered until that finishes, which is why this runs as an APP_INITIALIZER and
 * why it, rather than the router, owns the first navigation. See the forRoot() call at the bottom
 * of this file for why 'enabledBlocking' cannot be used to order these two steps.
 *
 * Note that the config is handed over with router.resetConfig(). Mutating the array passed to
 * forRoot() has no effect -- forRoot() reads it once, at module initialization.
 */
export function initializeUnauthNavRoutes(routeService: RouteService, appidService: AppidService, router: Router) {
    return async () => {
        try {
            // Deferred to break the import cycle described at the top of this file.
            const [{ SuperstructureComponent }, { SuperstructureEasysuiteComponent }] = await Promise.all([
                import('./gigmonster/components/superstructure/superstructure.component'),
                import('./easysuite/components/easysuite-superstructure/easysuite-superstructure.component')
            ]);

            // AppModule registers its own APP_INITIALIZER for the appID, but Angular starts every
            // initializer at once, so that one has not necessarily resolved and appID$ still holds
            // its initial null. Await the appID here and seed RouteService with it so getRoutes()
            // below reuses it instead of issuing a second /auth/getappid request.
            const appID = await appidService.getAppID();
            routeService.appID = appID;

            // Which superstructure renders a page is still per-app: GigMonster has its own, every
            // other app shares the EasySuite one. Goes away once the two are merged.
            const getSuperstructure = () => appID === GIGMONSTER_APP_ID
                ? SuperstructureComponent
                : SuperstructureEasysuiteComponent;

            // A cache means the user has navigated before, so it holds their real routes --
            // including the data.nav each superstructure reads to know which page to fetch. It has
            // to win here, and it has to be restored before the first navigation: the unauth set
            // below has no entry for a signed-in path like /login, so a refresh there would fall
            // through to '**', land on auth/login with no data.nav, and render an empty page.
            // NavService.loadNavsByCache() still runs from AppComponent.ngOnInit, but that is too
            // late to affect the first navigation; it now only serves as a fallback.
            const cachedRoutes = readCachedRoutes(getSuperstructure());
            if (cachedRoutes) {
                router.resetConfig(cachedRoutes);
                return;
            }

            try {
                const routes = await routeService.getRoutes();

                const newRoutes: Routes = [];

                // auth/login is where a user with no cached routes lands. It is a bootstrap step
                // rather than a real page: SuperstructureBaseComponent recognises the path, calls
                // NavService.updateNavs() to fetch and cache the routes for whatever role the user
                // turns out to have, then redirects to the first of those that has a page.
                newRoutes.push({ path: 'auth/login', component: getSuperstructure() });

                // The pages an unauthenticated visitor may reach directly (register, forgot
                // password, and so on), keyed by the label the backend gives them.
                routes.forEach((route: any) => {

                    let component;
                    // This is a temporary solution till we combine both EZS superstructure and GM superstructure into a single superstructure.
                    if (route.unauthNavLabel == 'venues' || route.unauthNavLabel == 'performers') {
                        component = SuperstructureComponent;
                    } else {
                        component = SuperstructureEasysuiteComponent;
                    }
                    newRoutes.push({ path: route.unauthNavLabel, component });
                });

                // Unknown paths go to auth/login, which then forwards to wherever the user's role
                // is allowed to start. Same wildcard NavService.updateNavs() appends, so the config
                // behaves identically whether it came from here or from a later nav update.
                newRoutes.push({ path: '**', redirectTo: 'auth/login' });
                router.resetConfig(newRoutes);
            } catch (error) {
                console.error('Error fetching routes:', error);

                // /auth/routes is down or the app is unknown. auth/login plus the wildcard is the
                // smallest config that still lets the user reach a login form, and leaves the
                // router with something to match rather than a dead outlet.
                router.resetConfig([
                    { path: 'auth/login', component: getSuperstructure() },
                    { path: '**', redirectTo: 'auth/login' }
                ]);
            }
        } finally {
            // The config is in place, so release the first navigation. In a finally because an
            // unexpected throw above must not skip it -- a config that is merely incomplete still
            // renders something, whereas never navigating leaves the outlet permanently empty.
            router.initialNavigation();
        }
    };
}
  

// forRoot() gets an empty array because the real routes are not known until initializeUnauthNavRoutes
// has talked to the backend; it installs them with router.resetConfig().
//
// initialNavigation is 'disabled' so that initializeUnauthNavRoutes can call
// router.initialNavigation() itself once those routes exist. 'enabledBlocking' looks like it would
// order the two correctly and does not: Angular invokes every APP_INITIALIZER in one pass and
// Promise.all's the results, so the router's blocking initializer navigates immediately while
// initializeUnauthNavRoutes is still awaiting its dynamic imports and HTTP calls. That navigation
// resolves against the empty array above, matches nothing, and the outlet stays blank for the life
// of the page -- the resetConfig() that arrives moments later updates the config but does not
// re-run the navigation.
@NgModule({
  imports: [RouterModule.forRoot([], { initialNavigation: 'disabled' })],
  exports: [RouterModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUnauthNavRoutes,
      deps: [RouteService, AppidService, Router],
      multi: true
    }
  ]
})
export class AppRoutingModule { }
